import { serverSignedPayloadMatches } from './stockCommandAckValidator.js';
import { openLocalDb } from '../db/local.js';
import {
  SharedTabletOperatorAssertionSchema,
  type SharedTabletOperatorAssertion,
} from '@trackeat/shared';
import { deepEqualJson } from '@trackeat/shared/sync';

export type ProductionLabelPrintIntentState =
  | 'pending'
  | 'printed'
  | 'failed'
  | 'voided';

export type ProductionLabelAuditKind = 'label_printed' | 'label_voided';

export interface ProductionLabelPrintIntent {
  id: string;
  tenant_id: string;
  business_id: string;
  production_id: string;
  command_id: string;
  audit_client_uuid: string;
  audit_kind: ProductionLabelAuditKind;
  label_payload: Record<string, unknown>;
  print_job: Record<string, unknown> | null;
  state: ProductionLabelPrintIntentState;
  requested_at: string;
  resolved_at: string | null;
  error: string | null;
  version: number;
}

export interface FinalizeProductionLabelPrintIntentInput {
  intentId: string;
  productionId: string;
  tenantId: string;
  businessId: string;
  outcome: Exclude<ProductionLabelPrintIntentState, 'pending'>;
  occurredAt: string;
  error?: string;
  operatorAssertion?: SharedTabletOperatorAssertion;
}

export interface PendingProductionLabelPrintOutcome {
  intent_id: string;
  tenant_id: string;
  business_id: string;
  production_id: string;
  command_id: string;
  outcome: FinalizeProductionLabelPrintIntentInput['outcome'];
  occurred_at: string;
  error: string | null;
  operator_assertion?: SharedTabletOperatorAssertion;
  intent_snapshot: ProductionLabelPrintIntent;
  status: 'pending' | 'sending';
  attempts: number;
  last_error: string | null;
  queued_at: string;
  updated_at: string;
}

export interface ProductionLabelPrintReconciliation {
  intent_id: string;
  command_id: string;
  tenant_id: string;
  business_id: string;
  production_id: string;
  outcome: 'printed' | 'voided';
  occurred_at: string;
  reason: string;
  intent_snapshot: ProductionLabelPrintIntent;
  status: 'reconciliation_required' | 'resolved';
  resolution: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export function mergeProductionLabelPrintIntentForAdoption(input: {
  local: ProductionLabelPrintIntent | null;
  remote: ProductionLabelPrintIntent;
  hasPendingOutcome: boolean;
}): ProductionLabelPrintIntent {
  const { local, remote, hasPendingOutcome } = input;
  if (!local) return remote;
  // finalize locally writes the terminal projection and durable outcome in one
  // SQLite transaction. A recovery RPC can still observe the server's older
  // pending row; never let that response resurrect a printable intent.
  if (hasPendingOutcome) return local;
  const localTerminal = local.state === 'printed' || local.state === 'voided';
  const remoteTerminal =
    remote.state === 'printed' || remote.state === 'voided';
  if (localTerminal && !remoteTerminal) return local;
  // Local failed/pending versions are optimistic; a durable server terminal
  // fact wins even if retries incremented the local counter further.
  if (remoteTerminal && !localTerminal) return remote;
  if (local.version > remote.version) return local;
  return remote;
}

/**
 * A terminal intent is authoritative once the server has written the matching
 * immutable audit fact. A device can retain its own outcome after another
 * device already completed that same intent, with a different clock value.
 * In that case retrying would only block the FIFO queue (and must never print
 * again), so adopt the authoritative row and remove that one local retry.
 */
export function adoptConfirmedProductionLabelPrintOutcome(input: {
  pending: PendingProductionLabelPrintOutcome;
  remoteIntent: unknown;
}): Promise<boolean> {
  return serializeIntentWork(async () => {
    const remote = parseIntent(input.remoteIntent);
    const pending = input.pending;
    const snapshot = pending.intent_snapshot;
    const isPhysicalTerminal = pending.outcome === 'printed'
      || (pending.outcome === 'voided' && snapshot.audit_kind === 'label_voided');
    if (
      !isPhysicalTerminal
      || remote.state !== pending.outcome
      || remote.resolved_at === null
      || remote.error !== pending.error
      || remote.id !== pending.intent_id
      || remote.tenant_id !== pending.tenant_id
      || remote.business_id !== pending.business_id
      || remote.production_id !== pending.production_id
      || remote.command_id !== pending.command_id
      || remote.audit_client_uuid !== snapshot.audit_client_uuid
      || remote.audit_kind !== snapshot.audit_kind
      || !deepEqualJson(remote.label_payload, snapshot.label_payload)
    ) {
      return false;
    }

    const db = await openLocalDb(null);
    const current = await db.query(
      `SELECT intent_snapshot_json, outcome
         FROM pending_label_print_outcomes_v1
        WHERE intent_id = ?`,
      [pending.intent_id],
    );
    const row = current.values?.[0] as {
      intent_snapshot_json: string;
      outcome: string;
    } | undefined;
    if (!row || row.outcome !== pending.outcome) return false;
    const currentSnapshot = parseIntent(
      JSON.parse(row.intent_snapshot_json) as unknown,
    );
    if (
      currentSnapshot.audit_client_uuid !== snapshot.audit_client_uuid
      || !deepEqualJson(currentSnapshot.label_payload, snapshot.label_payload)
    ) {
      return false;
    }

    const now = new Date().toISOString();
    await db.executeTransaction([
      {
        statement:
          `INSERT INTO masters_local(
             table_name, id, tenant_id, version, row, archived, updated_at
           ) VALUES (
             'production_label_print_intents_v1', ?, ?, ?, ?, 0, ?
           )
           ON CONFLICT(table_name,id) DO UPDATE SET
             tenant_id = excluded.tenant_id,
             version = excluded.version,
             row = excluded.row,
             archived = 0,
             updated_at = excluded.updated_at`,
        values: [
          remote.id,
          remote.tenant_id,
          remote.version,
          JSON.stringify(remote),
          now,
        ],
      },
      {
        statement: 'DELETE FROM pending_label_print_outcomes_v1 WHERE intent_id = ?',
        values: [pending.intent_id],
      },
      {
        statement: 'DELETE FROM production_label_intent_server_receipts_v1 WHERE intent_id = ?',
        values: [pending.intent_id],
      },
    ], true);
    return true;
  });
}

interface MasterRow {
  tenant_id: string | null;
  version: number;
  row: string;
  archived: number;
  updated_at: string;
}

interface OutcomeRow {
  intent_id: string;
  tenant_id: string;
  business_id: string;
  production_id: string;
  command_id: string | null;
  outcome: FinalizeProductionLabelPrintIntentInput['outcome'];
  occurred_at: string;
  error: string | null;
  operator_assertion_json?: string | null;
  intent_snapshot_json: string;
  status: 'pending' | 'sending';
  attempts: number;
  last_error: string | null;
  queued_at: string;
  updated_at: string;
}

interface ReconciliationRow {
  intent_id: string;
  command_id: string;
  tenant_id: string;
  business_id: string;
  production_id: string;
  outcome: 'printed' | 'voided';
  occurred_at: string;
  reason: string;
  intent_snapshot_json: string;
  status: 'reconciliation_required' | 'resolved';
  resolution: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let intentQueue: Promise<unknown> = Promise.resolve();

function serializeIntentWork<Result>(work: () => Promise<Result>): Promise<Result> {
  const run = intentQueue.then(work, work);
  intentQueue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseIntent(value: unknown): ProductionLabelPrintIntent {
  if (!isRecord(value)) throw new Error('production_label_intent_invalid');
  const state = value.state;
  const auditKind = value.audit_kind;
  if (
    typeof value.id !== 'string'
    || typeof value.tenant_id !== 'string'
    || typeof value.business_id !== 'string'
    || typeof value.production_id !== 'string'
    || typeof value.command_id !== 'string'
    || typeof value.audit_client_uuid !== 'string'
    || (auditKind !== 'label_printed' && auditKind !== 'label_voided')
    || !isRecord(value.label_payload)
    || (
      value.print_job !== null
      && value.print_job !== undefined
      && !isRecord(value.print_job)
    )
    || (
      state !== 'pending'
      && state !== 'printed'
      && state !== 'failed'
      && state !== 'voided'
    )
    || typeof value.requested_at !== 'string'
  ) {
    throw new Error('production_label_intent_invalid');
  }
  return {
    id: value.id,
    tenant_id: value.tenant_id,
    business_id: value.business_id,
    production_id: value.production_id,
    command_id: value.command_id,
    audit_client_uuid: value.audit_client_uuid,
    audit_kind: auditKind,
    label_payload: value.label_payload,
    print_job: (value.print_job as Record<string, unknown> | null | undefined) ?? null,
    state,
    requested_at: value.requested_at,
    resolved_at: typeof value.resolved_at === 'string' ? value.resolved_at : null,
    error: typeof value.error === 'string' ? value.error : null,
    version: Number(value.version ?? 0),
  };
}

function parseOutcomeRow(row: OutcomeRow): PendingProductionLabelPrintOutcome {
  const snapshot = parseIntent(JSON.parse(row.intent_snapshot_json) as unknown);
  const operatorAssertion = row.operator_assertion_json
    ? SharedTabletOperatorAssertionSchema.parse(
        JSON.parse(row.operator_assertion_json) as unknown,
      )
    : undefined;
  return {
    intent_id: row.intent_id,
    tenant_id: row.tenant_id,
    business_id: row.business_id,
    production_id: row.production_id,
    command_id: row.command_id ?? snapshot.command_id,
    outcome: row.outcome,
    occurred_at: row.occurred_at,
    error: row.error,
    operator_assertion: operatorAssertion,
    intent_snapshot: snapshot,
    status: row.status,
    attempts: Number(row.attempts),
    last_error: row.last_error,
    queued_at: row.queued_at,
    updated_at: row.updated_at,
  };
}

function parseReconciliationRow(
  row: ReconciliationRow,
): ProductionLabelPrintReconciliation {
  return {
    intent_id: row.intent_id,
    command_id: row.command_id,
    tenant_id: row.tenant_id,
    business_id: row.business_id,
    production_id: row.production_id,
    outcome: row.outcome,
    occurred_at: row.occurred_at,
    reason: row.reason,
    intent_snapshot: parseIntent(JSON.parse(row.intent_snapshot_json) as unknown),
    status: row.status,
    resolution: row.resolution,
    created_at: row.created_at,
    updated_at: row.updated_at,
    resolved_at: row.resolved_at,
  };
}

function validateFinalizeInput(
  input: FinalizeProductionLabelPrintIntentInput,
): void {
  for (const [name, value] of [
    ['intentId', input.intentId],
    ['productionId', input.productionId],
    ['tenantId', input.tenantId],
    ['businessId', input.businessId],
  ] as const) {
    if (!UUID_PATTERN.test(value)) {
      throw new Error(`production_label_intent_invalid_${name}`);
    }
  }
  if (!Number.isFinite(Date.parse(input.occurredAt))) {
    throw new Error('production_label_intent_invalid_occurred_at');
  }
  if (input.outcome === 'failed') {
    if (!input.error?.trim()) {
      throw new Error('production_label_intent_failure_error_required');
    }
  } else if (input.error !== undefined) {
    throw new Error('production_label_intent_error_only_for_failure');
  }
}

/**
 * Persiste outcome, proiezione intent e audit ottimistico nella stessa
 * transazione SQLite. Il chiamante puo quindi terminare il workflow fisico
 * senza attendere rete; il tick invia l'outcome alla RPC idempotente.
 */
export function finalizeProductionLabelPrintIntentLocally(
  input: FinalizeProductionLabelPrintIntentInput,
): Promise<void> {
  validateFinalizeInput(input);
  return serializeIntentWork(async () => {
    const db = await openLocalDb(null);
    const masterResult = await db.query(
      `SELECT tenant_id, version, row, archived, updated_at
         FROM masters_local
        WHERE table_name = 'production_label_print_intents_v1'
          AND id = ?`,
      [input.intentId],
    );
    const master = masterResult.values?.[0] as MasterRow | undefined;
    if (!master) throw new Error('production_label_intent_not_local');
    const intent = parseIntent(JSON.parse(master.row) as unknown);
    if (
      intent.id !== input.intentId
      || intent.production_id !== input.productionId
      || intent.tenant_id !== input.tenantId
      || intent.business_id !== input.businessId
    ) {
      throw new Error('production_label_intent_identity_mismatch');
    }
    if (
      (intent.audit_kind === 'label_voided' && input.outcome === 'printed')
      || (intent.audit_kind === 'label_printed' && input.outcome === 'voided')
    ) {
      throw new Error('production_label_intent_outcome_kind_mismatch');
    }
    if (
      (intent.state === 'printed' || intent.state === 'voided')
      && intent.state !== input.outcome
    ) {
      throw new Error('production_label_intent_already_resolved');
    }

    const pendingResult = await db.query(
      `SELECT intent_id, tenant_id, business_id, production_id, command_id, outcome,
              occurred_at, error, operator_assertion_json, intent_snapshot_json, status, attempts,
              last_error, queued_at, updated_at
         FROM pending_label_print_outcomes_v1
        WHERE intent_id = ?`,
      [input.intentId],
    );
    const pending = pendingResult.values?.[0] as OutcomeRow | undefined;
    if (
      pending
      && pending.outcome === input.outcome
      && pending.occurred_at === input.occurredAt
      && pending.error === (input.error?.trim() ?? null)
    ) {
      return;
    }
    if (pending && pending.status === 'sending') {
      throw new Error('production_label_intent_outcome_sending');
    }
    if (intent.state === input.outcome && !pending) return;

    const existingAuditResult = await db.query(
      `SELECT kind, payload
         FROM events_local
        WHERE id = ?`,
      [intent.audit_client_uuid],
    );
    const existingAudit = existingAuditResult.values?.[0] as {
      kind: string;
      payload: string;
    } | undefined;
    const shouldWriteAudit =
      input.outcome === 'printed'
      || (input.outcome === 'voided' && intent.audit_kind === 'label_voided');
    if (
      existingAudit
      && (
        !shouldWriteAudit
        || existingAudit.kind !== intent.audit_kind
        || JSON.stringify(JSON.parse(existingAudit.payload) as unknown)
             !== JSON.stringify(intent.label_payload)
      )
    ) {
      throw new Error('production_label_intent_audit_identity_conflict');
    }

    const error = input.error?.trim() ?? null;
    const nextIntent: ProductionLabelPrintIntent = {
      ...intent,
      state: input.outcome,
      resolved_at: input.occurredAt,
      error,
    };
    const now = new Date().toISOString();
    const tasks: Array<{ statement: string; values?: unknown[] }> = [
      {
        statement:
          `INSERT INTO pending_label_print_outcomes_v1(
             intent_id, tenant_id, business_id, production_id, command_id, outcome,
             occurred_at, error, operator_assertion_json, intent_snapshot_json, status, attempts,
             last_error, queued_at, updated_at
           ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'pending', 0, NULL, ?, ?)
           ON CONFLICT(intent_id) DO UPDATE SET
             tenant_id = excluded.tenant_id,
             business_id = excluded.business_id,
             production_id = excluded.production_id,
             command_id = excluded.command_id,
             outcome = excluded.outcome,
             occurred_at = excluded.occurred_at,
             error = excluded.error,
             operator_assertion_json = excluded.operator_assertion_json,
             intent_snapshot_json = excluded.intent_snapshot_json,
             status = 'pending',
             attempts = 0,
             last_error = NULL,
             updated_at = excluded.updated_at`,
        values: [
          input.intentId,
          input.tenantId,
          input.businessId,
          input.productionId,
          intent.command_id,
          input.outcome,
          input.occurredAt,
          error,
          input.operatorAssertion ? JSON.stringify(input.operatorAssertion) : null,
          JSON.stringify(nextIntent),
          now,
          now,
        ],
      },
      {
        statement:
          `UPDATE masters_local
              SET row = ?,
                  archived = 0,
                  updated_at = ?
            WHERE table_name = 'production_label_print_intents_v1'
              AND id = ?`,
        values: [JSON.stringify(nextIntent), now, input.intentId],
      },
    ];
    if (shouldWriteAudit && !existingAudit) {
      tasks.push({
        statement:
          `INSERT INTO events_local(
             id, server_id, tenant_id, kind, business_id, client_ts, server_ts, rev,
             payload, self_hash, prev_hash
           ) VALUES (?, NULL, ?, ?, ?, ?, NULL, NULL, ?, NULL, NULL)`,
        values: [
          intent.audit_client_uuid,
          intent.tenant_id,
          intent.audit_kind,
          intent.business_id,
          input.occurredAt,
          JSON.stringify(intent.label_payload),
        ],
      });
    }
    await db.executeTransaction(tasks, true);
  });
}

export function listProductionLabelPrintIntentsLocal(input: {
  states?: readonly ProductionLabelPrintIntentState[];
} = {}): Promise<ProductionLabelPrintIntent[]> {
  return serializeIntentWork(async () => {
    const states = [...new Set(input.states ?? [])];
    if (states.some((state) => (
      state !== 'pending'
      && state !== 'printed'
      && state !== 'failed'
      && state !== 'voided'
    ))) {
      throw new Error('production_label_intent_invalid_state_filter');
    }
    const db = await openLocalDb(null);
    const where = states.length > 0
      ? `AND json_extract(row, '$.state') IN (${states.map(() => '?').join(',')})`
      : '';
    const result = await db.query(
      `SELECT row
         FROM masters_local
        WHERE table_name = 'production_label_print_intents_v1'
          ${where}
        ORDER BY json_extract(row, '$.requested_at') DESC`,
      states,
    );
    return (result.values ?? []).map((value) => (
      parseIntent(JSON.parse((value as { row: string }).row) as unknown)
    ));
  });
}

export function adoptProductionLabelPrintIntents(
  values: readonly unknown[],
): Promise<void> {
  return serializeIntentWork(async () => {
    const intents = values.map(parseIntent);
    if (intents.length === 0) return;
    const db = await openLocalDb(null);
    const localResult = await db.query(
      `SELECT master.id, master.row,
              CASE WHEN outcome.intent_id IS NULL THEN 0 ELSE 1 END AS has_pending
         FROM masters_local master
         LEFT JOIN pending_label_print_outcomes_v1 outcome
           ON outcome.intent_id = master.id
        WHERE master.table_name = 'production_label_print_intents_v1'`,
    );
    const localById = new Map(
      (localResult.values ?? []).map((value) => {
        const row = value as {
          id: string;
          row: string;
          has_pending: number;
        };
        return [row.id, {
          intent: parseIntent(JSON.parse(row.row) as unknown),
          hasPendingOutcome: Number(row.has_pending) === 1,
        }] as const;
      }),
    );
    await db.executeTransaction(
      intents.flatMap((remote) => {
        const local = localById.get(remote.id);
        const intent = mergeProductionLabelPrintIntentForAdoption({
          local: local?.intent ?? null,
          remote,
          hasPendingOutcome: local?.hasPendingOutcome ?? false,
        });
        return [
          {
            statement:
              `INSERT INTO masters_local(
                 table_name, id, tenant_id, version, row, archived, updated_at
               ) VALUES (
                 'production_label_print_intents_v1', ?, ?, ?, ?, 0, datetime('now')
               )
               ON CONFLICT(table_name,id) DO UPDATE SET
                 tenant_id = excluded.tenant_id,
                 version = excluded.version,
                 row = excluded.row,
                 archived = 0,
                 updated_at = excluded.updated_at`,
            values: [
              intent.id,
              intent.tenant_id,
              intent.version,
              JSON.stringify(intent),
            ],
          },
          {
            statement:
              `INSERT INTO production_label_intent_server_receipts_v1(
                 intent_id, command_id, received_at
               ) VALUES (?, ?, datetime('now'))
               ON CONFLICT(intent_id) DO UPDATE SET
                 command_id = excluded.command_id,
                 received_at = excluded.received_at`,
            values: [remote.id, remote.command_id],
          },
        ];
      }),
      true,
    );
  });
}

/**
 * A label command is not eligible for remote delivery until every physical
 * print/void intent in the same compound has a durable local outcome.
 *
 * This closes the only race between the background sync timer and the printer:
 * the command + intent are already one SQLite transaction, while the command
 * remains held locally until paper outcome persistence completes.
 */
export async function isProductionLabelCommandReadyForPush(
  commandId: string,
  masterEffects: readonly { table: string; id: string }[],
): Promise<boolean> {
  const intentIds = masterEffects
    .filter((effect) => effect.table === 'production_label_print_intents_v1')
    .map((effect) => effect.id);
  if (intentIds.length === 0) return true;
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT intent_id, command_id, intent_snapshot_json
       FROM pending_label_print_outcomes_v1
      WHERE intent_id IN (${intentIds.map(() => '?').join(',')})`,
    intentIds,
  );
  const durable = new Map(
    (result.values ?? []).map((value) => {
      const row = value as {
        intent_id: string;
        command_id: string | null;
        intent_snapshot_json: string;
      };
      const snapshot = parseIntent(
        JSON.parse(row.intent_snapshot_json) as unknown,
      );
      return [row.intent_id, row.command_id ?? snapshot.command_id] as const;
    }),
  );
  if (intentIds.every((intentId) => durable.get(intentId) === commandId)) return true;
  const confirmed = await db.query(
    `SELECT master.id, master.row, audit.server_id, audit.rev, audit.self_hash,
            audit.payload AS audit_payload,
            receipt.command_id AS received_command_id
       FROM masters_local master
       LEFT JOIN events_local audit
         ON audit.id = json_extract(master.row, '$.audit_client_uuid')
        AND audit.tenant_id = master.tenant_id
        AND audit.business_id = json_extract(master.row, '$.business_id')
        AND audit.kind = json_extract(master.row, '$.audit_kind')
       LEFT JOIN production_label_intent_server_receipts_v1 receipt
         ON receipt.intent_id = master.id
        AND receipt.command_id = json_extract(master.row, '$.command_id')
      WHERE master.table_name = 'production_label_print_intents_v1'
        AND master.id IN (${intentIds.map(() => '?').join(',')})`,
    intentIds,
  );
  for (const value of confirmed.values ?? []) {
    const row = value as {
      id: string; row: string; server_id: string | null; rev: number | null;
      self_hash: string | null; audit_payload: string | null;
      received_command_id: string | null;
    };
    const intent = parseIntent(JSON.parse(row.row) as unknown);
    if (intent.command_id !== commandId) continue;
    const hasServerAudit = (intent.state === 'printed' || intent.state === 'voided')
      && row.rev !== null && Number(row.rev) > 0
      && typeof row.server_id === 'string' && UUID_PATTERN.test(row.server_id)
      && typeof row.self_hash === 'string' && /^[0-9a-f]{64}$/.test(row.self_hash)
      && row.audit_payload !== null
      && serverSignedPayloadMatches(JSON.parse(row.audit_payload) as unknown, intent.label_payload);
    // The command already reached the server if its intent was returned by
    // the recovery RPC or its exact immutable label audit is cached locally.
    // Retry the same command/UUIDs to finish its ACK; never print again.
    if (row.received_command_id === commandId || hasServerAudit) {
      durable.set(intent.id, commandId);
    }
  }
  return intentIds.every((intentId) => durable.get(intentId) === commandId);
}

/**
 * Move outcomes whose compound was permanently rejected into a separate,
 * durable reconciliation ledger. The master intent may already have been
 * removed by optimistic rollback, so the outcome snapshot is the source of
 * truth. Nothing here creates compensating stock facts.
 */
export function reconcileRejectedProductionLabelPrintOutcomes(): Promise<number> {
  return serializeIntentWork(async () => {
    const db = await openLocalDb(null);
    const result = await db.query(
      `SELECT outcome.intent_id, outcome.tenant_id, outcome.business_id,
              outcome.production_id, outcome.command_id, outcome.outcome,
              outcome.occurred_at, outcome.error, outcome.intent_snapshot_json,
              outcome.status, outcome.attempts, outcome.last_error,
              outcome.queued_at, outcome.updated_at,
              finalization.reason AS rejection_reason
         FROM pending_label_print_outcomes_v1 outcome
         JOIN stock_command_finalizations finalization
           ON finalization.command_id = COALESCE(
                outcome.command_id,
                json_extract(outcome.intent_snapshot_json, '$.command_id')
              )
          AND finalization.outcome = 'permanently_rejected'
        ORDER BY outcome.queued_at`,
    );
    const rows = (result.values ?? []) as Array<
      OutcomeRow & { rejection_reason: string | null }
    >;
    if (rows.length === 0) return 0;
    const now = new Date().toISOString();
    const tasks: Array<{ statement: string; values?: unknown[] }> = [];
    for (const row of rows) {
      const outcome = parseOutcomeRow(row);
      const requiresPhysicalReconciliation = outcome.outcome === 'printed'
        || (
          outcome.outcome === 'voided'
          && outcome.intent_snapshot.audit_kind === 'label_voided'
        );
      if (requiresPhysicalReconciliation) {
        tasks.push({
          statement:
            `INSERT INTO production_label_reconciliations_v1(
               intent_id, command_id, tenant_id, business_id, production_id,
               outcome, occurred_at, reason, intent_snapshot_json, status,
               resolution, created_at, updated_at, resolved_at
             ) VALUES (
               ?, ?, ?, ?, ?, ?, ?, ?, ?, 'reconciliation_required',
               NULL, ?, ?, NULL
             )
             ON CONFLICT(intent_id) DO UPDATE SET
               reason = excluded.reason,
               intent_snapshot_json = excluded.intent_snapshot_json,
               status = CASE
                 WHEN production_label_reconciliations_v1.status = 'resolved'
                 THEN production_label_reconciliations_v1.status
                 ELSE 'reconciliation_required'
               END,
               updated_at = excluded.updated_at`,
          values: [
            outcome.intent_id,
            outcome.command_id,
            outcome.tenant_id,
            outcome.business_id,
            outcome.production_id,
            outcome.outcome,
            outcome.occurred_at,
            row.rejection_reason?.trim() || 'stock_command_permanently_rejected',
            JSON.stringify(outcome.intent_snapshot),
            now,
            now,
          ],
        });
      }
      // The optimistic audit never reached the append-only server chain. Keep
      // the physical incident above, but remove the local ghost history row.
      tasks.push(
        {
          statement:
            `DELETE FROM events_local
              WHERE id = ?
                AND rev IS NULL`,
          values: [outcome.intent_snapshot.audit_client_uuid],
        },
        {
          statement:
            'DELETE FROM pending_label_print_outcomes_v1 WHERE intent_id = ?',
          values: [outcome.intent_id],
        },
        {
          statement:
            `DELETE FROM production_label_intent_server_receipts_v1
              WHERE intent_id = ?`,
          values: [outcome.intent_id],
        },
      );
    }
    await db.executeTransaction(tasks, true);
    return rows.length;
  });
}

export async function listProductionLabelPrintOutcomesForScope(input: {
  tenantId: string;
  businessId: string;
}): Promise<PendingProductionLabelPrintOutcome[]> {
  const db = await openLocalDb(null);
  const result = await db.query(
      `SELECT intent_id, tenant_id, business_id, production_id, command_id,
            outcome, occurred_at, error, operator_assertion_json, intent_snapshot_json, status, attempts,
            last_error, queued_at, updated_at
       FROM pending_label_print_outcomes_v1
      WHERE tenant_id = ?
        AND business_id = ?
        AND status IN ('pending','sending')
      ORDER BY queued_at DESC`,
    [input.tenantId, input.businessId],
  );
  const outcomes: PendingProductionLabelPrintOutcome[] = [];
  for (const value of result.values ?? []) {
    const row = value as OutcomeRow;
    try {
      outcomes.push(parseOutcomeRow(row));
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      await db.run(
        `UPDATE pending_label_print_outcomes_v1
            SET status = 'pending', attempts = attempts + 1,
                last_error = ?, updated_at = ?
          WHERE intent_id = ?`,
        [
          `client_prepare_failed:${reason}`,
          new Date().toISOString(),
          row.intent_id,
        ],
      );
    }
  }
  return outcomes;
}

export async function listProductionLabelPrintReconciliations(input: {
  tenantId: string;
  businessId: string;
  includeResolved?: boolean;
}): Promise<ProductionLabelPrintReconciliation[]> {
  await reconcileRejectedProductionLabelPrintOutcomes();
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT intent_id, command_id, tenant_id, business_id, production_id,
            outcome, occurred_at, reason, intent_snapshot_json, status,
            resolution, created_at, updated_at, resolved_at
       FROM production_label_reconciliations_v1
      WHERE tenant_id = ?
        AND business_id = ?
        ${input.includeResolved ? '' : "AND status = 'reconciliation_required'"}
      ORDER BY created_at DESC`,
    [input.tenantId, input.businessId],
  );
  return (result.values ?? []).map((value) => (
    parseReconciliationRow(value as ReconciliationRow)
  ));
}

export function resolveProductionLabelPrintReconciliation(input: {
  intentId: string;
  tenantId: string;
  businessId: string;
  resolvedAt?: string;
}): Promise<void> {
  return serializeIntentWork(async () => {
    if (
      !UUID_PATTERN.test(input.intentId)
      || !UUID_PATTERN.test(input.tenantId)
      || !UUID_PATTERN.test(input.businessId)
    ) {
      throw new Error('production_label_reconciliation_identity_invalid');
    }
    const resolvedAt = input.resolvedAt ?? new Date().toISOString();
    if (!Number.isFinite(Date.parse(resolvedAt))) {
      throw new Error('production_label_reconciliation_resolved_at_invalid');
    }
    const db = await openLocalDb(null);
    const current = await db.query(
      `SELECT status
         FROM production_label_reconciliations_v1
        WHERE intent_id = ?
          AND tenant_id = ?
          AND business_id = ?`,
      [input.intentId, input.tenantId, input.businessId],
    );
    const row = current.values?.[0] as { status: string } | undefined;
    if (!row) throw new Error('production_label_reconciliation_not_found');
    if (row.status === 'resolved') return;
    await db.run(
      `UPDATE production_label_reconciliations_v1
          SET status = 'resolved',
              resolution = 'label_removed_or_resolved',
              resolved_at = ?,
              updated_at = ?
        WHERE intent_id = ?
          AND tenant_id = ?
          AND business_id = ?`,
      [
        resolvedAt,
        resolvedAt,
        input.intentId,
        input.tenantId,
        input.businessId,
      ],
    );
  });
}

export async function listPendingProductionLabelPrintOutcomes(
  limit = 20,
  excludedIntentIds: ReadonlySet<string> = new Set(),
): Promise<PendingProductionLabelPrintOutcome[]> {
  await reconcileRejectedProductionLabelPrintOutcomes();
  const db = await openLocalDb(null);
  const excludedIds = [...excludedIntentIds];
  const exclusionFilter = excludedIds.length > 0
    ? `AND outcome.intent_id NOT IN (${excludedIds.map(() => '?').join(',')})`
    : '';
  const result = await db.query(
      `SELECT intent_id, tenant_id, business_id, production_id, outcome,
            command_id, occurred_at, error, operator_assertion_json, intent_snapshot_json, status, attempts,
            last_error, queued_at, updated_at
      FROM pending_label_print_outcomes_v1 outcome
      WHERE outcome.status IN ('pending','sending')
        ${exclusionFilter}
        AND (outcome.last_error IS NULL
          OR outcome.last_error NOT LIKE 'client_prepare_failed:%'
          OR julianday(outcome.updated_at) <= julianday(?))
        AND (
          EXISTS (
            SELECT 1
              FROM stock_command_finalizations finalization
             WHERE finalization.command_id = COALESCE(
                     outcome.command_id,
                     CASE WHEN json_valid(outcome.intent_snapshot_json)
                       THEN json_extract(outcome.intent_snapshot_json, '$.command_id')
                       ELSE NULL
                     END
                   )
               AND finalization.outcome = 'acknowledged'
          )
          OR EXISTS (
            SELECT 1
              FROM production_label_intent_server_receipts_v1 receipt
             WHERE receipt.intent_id = outcome.intent_id
          )
        )
      ORDER BY attempts ASC, queued_at ASC
      LIMIT ?`,
    [
      ...excludedIds,
      new Date(Date.now() - 300_000).toISOString(),
      limit,
    ],
  );
  const outcomes: PendingProductionLabelPrintOutcome[] = [];
  for (const value of result.values ?? []) {
    const row = value as OutcomeRow;
    try {
      outcomes.push(parseOutcomeRow(row));
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      await db.run(
        `UPDATE pending_label_print_outcomes_v1
            SET status = 'pending', attempts = attempts + 1,
                last_error = ?, updated_at = ?
          WHERE intent_id = ?`,
        [
          `client_prepare_failed:${reason}`,
          new Date().toISOString(),
          row.intent_id,
        ],
      );
    }
  }
  return outcomes;
}

export async function beginProductionLabelPrintOutcomeSend(
  intentId: string,
): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `UPDATE pending_label_print_outcomes_v1
        SET status = 'sending',
            updated_at = ?
      WHERE intent_id = ?`,
    [new Date().toISOString(), intentId],
  );
}

export async function retryProductionLabelPrintOutcome(
  intentId: string,
  reason: string,
): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `UPDATE pending_label_print_outcomes_v1
        SET status = 'pending',
            attempts = attempts + 1,
            last_error = ?,
            updated_at = ?
      WHERE intent_id = ?`,
    [reason, new Date().toISOString(), intentId],
  );
}

export async function acknowledgeProductionLabelPrintOutcome(
  intentId: string,
): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    'DELETE FROM pending_label_print_outcomes_v1 WHERE intent_id = ?',
    [intentId],
  );
}

export async function countPendingProductionLabelPrintOutcomes(): Promise<number> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT COUNT(*) AS count
       FROM pending_label_print_outcomes_v1
      WHERE status IN ('pending','sending')`,
  );
  return Number(
    (result.values?.[0] as { count?: number } | undefined)?.count ?? 0,
  );
}
